import {memo} from 'react'
import {useWindowDimensions, View} from 'react-native'
import {
  AtUri,
  type $Typed,
  AppBskyEmbedRecord,
  type AppBskyFeedDefs,
  AppBskyFeedPost,
} from '@atproto/api'

import {makeProfileLink} from '#/lib/routes/links'

import {
  isClassroomPostVisibleTo,
  readClassroomPostMark,
} from '#/state/classrooms'
import {useClassroomPostRole} from '#/state/classrooms/account'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, native, useTheme, web} from '#/alf'
import {CapsuleFeedEmbed} from '#/components/capsules/CapsuleFeedEmbed'
import {ClassroomPostLocked} from '#/components/classrooms/ClassroomPostByline'
import {
  FlashBookFeedEmbed,
  hasFlashbookEmbed,
} from '#/components/flashbooks/FlashBookFeedEmbed'
import {
  hasModuleEmbed,
  ModuleFeedEmbed,
} from '#/components/modules/ModuleFeedEmbed'
import {Embed, PostEmbedViewContext} from '#/components/Post/Embed'
import {Text} from '#/components/Typography'
import {MessageContextProvider} from './MessageContext'

const BORDER_RADIUS = 20
const SQUARED_BORDER_RADIUS = 4

function viewRecordToPostView(
  record: AppBskyEmbedRecord.ViewRecord,
): AppBskyFeedDefs.PostView {
  return {
    $type: 'app.bsky.feed.defs#postView',
    uri: record.uri,
    cid: record.cid,
    author: record.author,
    record: record.value,
    embed: record.embeds?.[0],
    labels: record.labels,
    indexedAt: record.indexedAt,
  }
}

let MessageItemEmbed = ({
  embed,
  isFromSelf,
  isGroupChat,
  squaredTopCorner,
  squaredBottomCorner,
}: {
  embed: $Typed<AppBskyEmbedRecord.View>
  isFromSelf: boolean
  isGroupChat: boolean
  squaredTopCorner: boolean
  squaredBottomCorner: boolean
}): React.ReactNode => {
  const t = useTheme()
  const screen = useWindowDimensions()

  const embedRecord = AppBskyEmbedRecord.isViewRecord(embed.record)
    ? (embed.record as unknown as AppBskyEmbedRecord.ViewRecord)
    : null
  const embedValue = embedRecord?.value
  const isCapsule = !!(embedValue?.capsule || embedValue?.type === 'capsule')
  /*
   * A book sent in a message is drawn as a book, not as a link: the same card the
   * feed uses, so a book looks like itself wherever it is shared.
   */
  const isFlashbook = hasFlashbookEmbed({
    record: embedValue,
    flashbook: embedValue?.flashbook,
  })
  /*
   * A module sent in a message is a playlist, and it is drawn as one: the card
   * carries its own carousel of capsules, so a shared course is a thing the
   * person receiving it can see and open rather than a caption they have to take
   * on trust.
   */
  const isModule = hasModuleEmbed({
    record: embedValue,
    module: embedValue?.module,
  })

  const containerStyle = [
    !isFromSelf && isGroupChat && a.ml_sm,
    native({
      flexBasis: 0,
      width: Math.min(screen.width, 600) / 1.4,
    }),
    web({
      width: '100%',
      minWidth: 280,
      maxWidth: 360,
    }),
  ]

  const cornerStyle = isFromSelf
    ? {
        borderBottomRightRadius: squaredBottomCorner
          ? SQUARED_BORDER_RADIUS
          : BORDER_RADIUS,
        borderTopRightRadius: squaredTopCorner
          ? SQUARED_BORDER_RADIUS
          : BORDER_RADIUS,
      }
    : {
        borderBottomLeftRadius: squaredBottomCorner
          ? SQUARED_BORDER_RADIUS
          : BORDER_RADIUS,
        borderTopLeftRadius: squaredTopCorner
          ? SQUARED_BORDER_RADIUS
          : BORDER_RADIUS,
      }

  /*
   * A room's post, sent in a message.
   *
   * The point of sending a room's post is that the people it was written for see
   * it — the sender is one of them and the post is theirs to send — so the card is
   * drawn for whoever is reading the message, and a reader the post was not
   * written for is told the rule instead of being shown the post
   * (`isClassroomPostVisibleTo`). This is where that reader meets it: the
   * recipient of a message, on a device that has never heard of the room.
   */
  const classroomMark = readClassroomPostMark({record: embedValue})
  const classroomRole = useClassroomPostRole(classroomMark)
  const classroomLocked =
    !!classroomMark &&
    !isClassroomPostVisibleTo(classroomMark, {role: classroomRole})

  if (classroomLocked && classroomMark && embedRecord) {
    return (
      <View style={containerStyle} testID="messageClassroomLocked">
        <View
          style={[
            a.rounded_xl,
            a.p_xs,
            a.mt_sm,
            {
              backgroundColor: isFromSelf
                ? t.palette.primary_50
                : t.palette.contrast_50,
            },
            cornerStyle,
          ]}>
          <ClassroomPostLocked
            mark={classroomMark}
            author={embedRecord.author}
            timestamp={embedRecord.indexedAt}
            postHref={makeProfileLink(
              embedRecord.author,
              'post',
              new AtUri(embedRecord.uri).rkey,
            )}
          />
        </View>
      </View>
    )
  }

  if ((isCapsule || isFlashbook || isModule) && embedRecord) {
    const post = viewRecordToPostView(embedRecord)
    const author = embedRecord.author
    const postText =
      AppBskyFeedPost.isRecord(embedRecord.value) &&
      typeof embedRecord.value.text === 'string' &&
      embedRecord.value.text.trim()
        ? embedRecord.value.text
        : undefined

    return (
      <View style={containerStyle}>
        <View
          style={[
            a.rounded_xl,
            a.overflow_hidden,
            {
              backgroundColor: isFromSelf
                ? t.palette.primary_50
                : t.palette.contrast_50,
            },
            cornerStyle,
          ]}>
          {/* Author row */}
          <View
            style={[a.flex_row, a.align_center, a.gap_xs, a.px_sm, a.pt_sm]}>
            <UserAvatar type="user" size={18} avatar={author.avatar} />
            <Text
              style={[a.text_sm, a.font_bold, t.atoms.text]}
              numberOfLines={1}>
              {author.displayName || author.handle}
            </Text>
          </View>

          {/* Post text as title/description */}
          {postText ? (
            <Text
              style={[
                a.text_sm,
                t.atoms.text_contrast_medium,
                a.px_sm,
                a.pt_2xs,
                a.pb_xs,
              ]}
              numberOfLines={3}>
              {postText}
            </Text>
          ) : (
            <View style={[a.pb_xs]} />
          )}

          {/**
            The card the work is drawn by: playlist, book, or capsule.

            A module's card has no padding of its own — in a post it fills the body
            of the post and the post's own gutter is its edge (see
            `#/view/com/posts/PostFeedItem`) — so inside a bubble it needs the
            bubble's padding put back, or its chips and its button would sit on the
            bubble's rounded edge.
          */}
          {isModule ? (
            <View style={[a.px_sm, a.pb_sm]}>
              <ModuleFeedEmbed post={post} />
            </View>
          ) : null}
          {isFlashbook ? <FlashBookFeedEmbed post={post} /> : null}
          {isCapsule ? <CapsuleFeedEmbed post={post} /> : null}
        </View>
      </View>
    )
  }

  return (
    <MessageContextProvider>
      <View
        style={[
          ...containerStyle,
          // Cancel out the embed's internal a.mt_sm so the container's
          // CLUSTERED_MESSAGE_GAP (2px) is the only spacing applied
          {marginTop: -a.mt_sm.marginTop},
        ]}>
        <View>
          <Embed
            embed={embed}
            allowNestedQuotes
            viewContext={PostEmbedViewContext.ChatMessage}
            style={[
              a.rounded_xl,
              a.overflow_hidden,
              a.border_0,
              isFromSelf
                ? {backgroundColor: t.palette.primary_50, ...cornerStyle}
                : {backgroundColor: t.palette.contrast_50, ...cornerStyle},
            ]}
          />
        </View>
      </View>
    </MessageContextProvider>
  )
}
MessageItemEmbed = memo(MessageItemEmbed)
export {MessageItemEmbed}
